import axios from 'axios'

// Backend API Configuration
const API_URL = '/api'  // Use relative URL so it goes through nginx proxy

// Auth-specific axios instance with CSRF support
const authAxios = axios.create({
  baseURL: API_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
  // Ensure HttpOnly cookies (e.g., jpdz_token) are sent on subdomain requests
  withCredentials: true,
})

// Add request interceptor to include auth token
authAxios.interceptors.request.use((config) => {
  // Do NOT attach Authorization for public auth endpoints
  const url = (config.url || '').toLowerCase()
  const isPublicAuth =
    url.includes('/auth/login') ||
    url.includes('/auth/register') ||
    url.includes('/auth/register- verified') ||
    url.includes('/auth/send-otp') ||
    url.includes('/auth/verify-otp') ||
    url.includes('/auth/google')

  if (!isPublicAuth) {
    const token = localStorage.getItem('jpdz_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
  }
  return config
})

// Response interceptor for better error handling
authAxios.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status
    const url = (error.config?.url || '').toLowerCase()
    const isLogin = url.includes('/auth/login')

    if (status === 401 && !isLogin) {
      // Token expired/invalid on protected calls - just clear token
      // Don't redirect with window.location.href as it causes reload loops
      localStorage.removeItem('jpdz_token')
    }
    return Promise.reject(error)
  }
)

interface User {
  id: number
  username: string
  email: string
  full_name?: string
}

interface LoginResponse {
  token: string
  user: User
}

interface SignupResponse {
  token?: string
  user?: User
  requires_verification?: boolean
  success?: boolean
  message?: string
  email?: string
  user_id?: number
}

class AuthService {

  async login(username: string, password: string): Promise<LoginResponse> {
    const response = await authAxios.post('/auth/login', {
      email: username, // Backend expects 'email' field
      password,
    })

    // Transform backend response to match frontend expectations
    const { token, user } = response.data

    // Store token in localStorage for consistency
    localStorage.setItem('jpdz_token', token)

    return {
      token,
      user: {
        id: parseInt(user.id),
        username: user.email, // Use email as username since backend uses email
        email: user.email,
        full_name: user.full_name || user.first_name || '',
      }
    }
  }

  async signup(email: string, username: string, password: string, full_name?: string): Promise<SignupResponse> {
    const response = await authAxios.post('/auth/register', {
      email,
      password,
      first_name: full_name || username,
      last_name: '',
    })

    // Handle both immediate token response and verification required response
    const data = response.data

    if (data.token && data.user) {
      // Immediate login response
      localStorage.setItem('jpdz_token', data.token)
      return {
        token: data.token,
        user: {
          id: parseInt(data.user.id),
          username: data.user.username,
          email: data.user.email,
          full_name: data.user.full_name,
        }
      }
    } else {
      // Verification required response
      return {
        requires_verification: data.requires_verification,
        success: data.success,
        message: data.message,
        email: data.email,
        user_id: data.user_id
      }
    }
  }

  async sendOtp(email: string, username?: string): Promise<void> {
    await authAxios.post('/auth/send-otp', {
      email,
      username: username || 'User'
    })
  }

  async verifyOtp(email: string, otp: string): Promise<void> {
    await authAxios.post('/auth/verify-otp', {
      email,
      otp,
    })
  }

  async registerWithVerification(username: string, email: string, password: string, full_name?: string): Promise<SignupResponse> {
    const response = await authAxios.post('/auth/register-verified', {
      username,
      email,
      password,
      full_name,
    })

    // Transform backend response to match frontend expectations
    const { token, user_id, username: userName, email: userEmail, full_name: fullName } = response.data

    // Store token in localStorage for consistency
    localStorage.setItem('jpdz_token', token)

    return {
      token,
      user: {
        id: parseInt(user_id),
        username: userName,
        email: userEmail,
        full_name: fullName,
      }
    }
  }

  async logout(): Promise<void> {
    try {
      await authAxios.post('/auth/logout')
    } catch (error) {
    }
    localStorage.removeItem('jpdz_token')
  }

  async verify(token?: string): Promise<any> {

    try {
      const response = await authAxios.get('/auth/verify')
      return response.data
    } catch (error) {
      if (error instanceof Error) {
      }
      if ((error as any).response) {
      }
      throw error
    }
  }
}

export const authService = new AuthService()
